import { create } from "zustand";
import type { ConversationMessage, ItemCommsMessage } from "@forgepoint/shared";
import {
  ConversationMessagesResponseSchema,
  ItemCommsMessagesResponseSchema,
} from "@forgepoint/shared";
import { fetchApi } from "@/lib/api";

type LoadState = { isLoading: boolean; error: string | null };

type ItemCommsMessagesState = {
  messagesByItemId: Record<string, ItemCommsMessage[]>;
  loadingByItemId: Record<string, LoadState>;
  conversationsById: Record<string, ConversationMessage[]>;
  conversationLoadingById: Record<string, LoadState>;
  fetchMessages: (itemId: string) => Promise<void>;
  fetchConversation: (conversationId: string) => Promise<void>;
};

/**
 * Per-item referenced comms messages and the conversations behind them, for the
 * item editor's comms messages tab and its full-conversation modal. Entities
 * are keyed by their own id with a per-id loading state, so tabs and modals
 * load independently.
 */
export const useItemCommsMessagesStore = create<ItemCommsMessagesState>(
  (set) => ({
    messagesByItemId: {},
    loadingByItemId: {},
    conversationsById: {},
    conversationLoadingById: {},
    fetchMessages: async (itemId: string) => {
      set((state) => ({
        loadingByItemId: {
          ...state.loadingByItemId,
          [itemId]: { isLoading: true, error: null },
        },
      }));
      try {
        const response = await fetchApi(
          `items/${itemId}/comms-messages`,
          "GET",
        );
        if (!response.ok) {
          throw new Error(response.error ?? "Failed to load messages");
        }
        const parsed = ItemCommsMessagesResponseSchema.parse(response.data);
        set((state) => ({
          messagesByItemId: {
            ...state.messagesByItemId,
            [itemId]: parsed.messages,
          },
          loadingByItemId: {
            ...state.loadingByItemId,
            [itemId]: { isLoading: false, error: null },
          },
        }));
      } catch (error) {
        set((state) => ({
          loadingByItemId: {
            ...state.loadingByItemId,
            [itemId]: {
              isLoading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to load messages",
            },
          },
        }));
      }
    },
    fetchConversation: async (conversationId: string) => {
      set((state) => ({
        conversationLoadingById: {
          ...state.conversationLoadingById,
          [conversationId]: { isLoading: true, error: null },
        },
      }));
      try {
        const response = await fetchApi(
          `comms/conversations/${conversationId}/messages`,
          "GET",
        );
        if (!response.ok) {
          throw new Error(response.error ?? "Failed to load conversation");
        }
        const parsed = ConversationMessagesResponseSchema.parse(response.data);
        set((state) => ({
          conversationsById: {
            ...state.conversationsById,
            [conversationId]: parsed.messages,
          },
          conversationLoadingById: {
            ...state.conversationLoadingById,
            [conversationId]: { isLoading: false, error: null },
          },
        }));
      } catch (error) {
        set((state) => ({
          conversationLoadingById: {
            ...state.conversationLoadingById,
            [conversationId]: {
              isLoading: false,
              error:
                error instanceof Error
                  ? error.message
                  : "Failed to load conversation",
            },
          },
        }));
      }
    },
  }),
);
